import { Col, Container, Row } from "reactstrap";
import s from "../organizationSetup/styles.scss";
import WizardBreadcrumb from "../common/WizardBreadcrumb";
import React, { useMemo } from "react";
import { Wizard } from "react-use-wizard";
import WizardStepper from "../components/WizardStepper";
import StepTransition from "../components/StepTransition";
import withStyles from "isomorphic-style-loader/withStyles";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { serviceCaller, ServiceCallerProps } from "../../../../composition";
import LoadingSpinner from "../../../../../toolkit/loadingSpinner";
import { useBrandSetupData } from "./hooks/useBrandSetupData";
import { connect } from "react-redux";
import { firebaseConnect, isLoaded } from "react-redux-firebase";
import { get } from "lodash";
import {
  PaymentSetupData,
  usesExternalGivingPage,
} from "../../../../../utils/firebase/paymentSetup";
import { getBrandSetupStepIndices } from "../../../../../utils/firebase/brandSetup";
import { BrandSetupWizardContext, CustomizeBackOverride } from "./context";
import SnackbarNotification from "../../../../../toolkit/snackbarNotification";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import { buildBrandSetupWizardSteps } from "./brandSetupWizardSteps";

interface BrandSetupWizardProps extends ServiceCallerProps {
  embedded?: boolean;
  onExit?: () => void;
  usesExternalGivingPage?: boolean;
}

const BrandSetupWizard: React.FC<BrandSetupWizardProps> = ({
  embedded = true,
  onExit,
  currentCharity,
  usesExternalGivingPage: usesExternalGivingPageProp = false,
}) => {
  const { snackbarProps, showSnackbar } = useSnackbar();
  const [customizeBackOverride, setCustomizeBackOverride] =
    React.useState<CustomizeBackOverride | null>(null);

  const methods = useForm({
    mode: "onSubmit",
    reValidateMode: "onChange",
    resolver: yupResolver(
      yup.object().shape({
        brandSetupMethod: yup.string(),
        brandUrl: yup.string().nullable(),
        useBrandName: yup.boolean(),
        useDescription: yup.boolean(),
        useLogos: yup.boolean(),
        useFavicon: yup.boolean(),
        useColors: yup.boolean(),
        useLinks: yup.boolean(),
        useFonts: yup.boolean(),
        useImages: yup.boolean(),
      })
    ),
    defaultValues: {
      brandSetupMethod: "fetch",
      useBrandName: true,
      useDescription: true,
      useLogos: true,
      useFavicon: true,
      useColors: true,
      useLinks: true,
      useFonts: true,
      useImages: true,
    },
  });

  const watchedSetupMethod = methods.watch("brandSetupMethod");

  const { loading, startingStep, error, setupMethod } = useBrandSetupData({
    charityId: currentCharity?.id,
    methods,
  });

  const isManualSetup =
    (watchedSetupMethod || setupMethod || "fetch") === "manual";

  const activeWizardSteps = useMemo(
    () =>
      buildBrandSetupWizardSteps(
        isManualSetup,
        usesExternalGivingPageProp
      ),
    [isManualSetup, usesExternalGivingPageProp]
  );

  const { customizeStepIndex } = useMemo(
    () =>
      getBrandSetupStepIndices(
        isManualSetup,
        usesExternalGivingPageProp
      ),
    [isManualSetup, usesExternalGivingPageProp]
  );

  const brandSetupWizardSchema = useMemo(
    () =>
      yup.object().shape(
        activeWizardSteps.reduce(
          (acc, step) => ({
            ...acc,
            ...step.schema.fields,
          }),
          {}
        )
      ),
    [activeWizardSteps]
  );

  React.useEffect(() => {
    methods.clearErrors();
  }, [brandSetupWizardSchema, methods]);

  const wizardContextValue = useMemo(
    () => ({
      usesExternalGivingPage: usesExternalGivingPageProp,
      customizeStepIndex,
      onExit,
      showSnackbar,
      customizeBackOverride,
      setCustomizeBackOverride,
    }),
    [
      usesExternalGivingPageProp,
      customizeStepIndex,
      onExit,
      showSnackbar,
      customizeBackOverride,
    ]
  );

  const renderMainContent = () => {
    if (error) {
      return (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "300px" }}
        >
          <div className="text-center">
            <h5 className="text-danger">Unable to load brand setup</h5>
            <p className="text-muted">{error}</p>
          </div>
        </div>
      );
    }

    return (
      <BrandSetupWizardContext.Provider value={wizardContextValue}>
        <FormProvider {...methods}>
          <form noValidate className={s.fadeIn}>
            <Wizard
              startIndex={startingStep}
              header={
                <WizardStepper
                  steps={activeWizardSteps}
                  onBack={customizeBackOverride?.onBack ?? onExit}
                  backLabel={customizeBackOverride?.label}
                  showBack={!embedded}
                />
              }
              wrapper={
                <StepTransition
                  contentClassName={embedded ? undefined : s.wizardStepPanel}
                />
              }
            >
              {activeWizardSteps.map(({ Component }, index) => (
                <Component key={index} onExit={onExit} />
              ))}
            </Wizard>
          </form>
        </FormProvider>
      </BrandSetupWizardContext.Provider>
    );
  };

  return (
    <>
      <Container fluid className={embedded ? "vh-100" : s.fullScreenContainer}>
        <Row className="h-100">
          <Col md="12" className={s.wizardContainer}>
            {!embedded && loading && (
              <WizardBreadcrumb
                onBack={customizeBackOverride?.onBack ?? onExit}
                backLabel={customizeBackOverride?.label}
                showBreadcrumb
              />
            )}
            {loading ? (
              <div className="mt-5">
                <LoadingSpinner
                  title="Loading Brand Setup"
                  message="Please wait while we load your data and initialize the wizard."
                  className={s.fadeIn}
                />
              </div>
            ) : (
              <div className={embedded ? undefined : s.wizardShell}>
                {renderMainContent()}
              </div>
            )}
          </Col>
        </Row>
      </Container>

      <SnackbarNotification
        {...snackbarProps}
        position={{ vertical: "top", horizontal: "center" }}
      />
    </>
  );
};

const mapStateToProps = (state: any) => {
  const { user, firebase } = state;
  const charityId = user.currentCharity?.id || "";

  const paymentSetupLoaded =
    charityId &&
    isLoaded(firebase.data[charityId]) &&
    isLoaded(get(firebase, `data.${charityId}.charitySetup.payment`));

  const paymentSetupData: PaymentSetupData | null = paymentSetupLoaded
    ? get(firebase, `data.${charityId}.charitySetup.payment`, null)
    : null;

  return {
    usesExternalGivingPage: usesExternalGivingPage(paymentSetupData),
  };
};

export default connect(mapStateToProps)(
  firebaseConnect((_, store) => {
    const charityId = store.getState().user.currentCharity?.id;
    return charityId ? [`${charityId}/charitySetup/payment`] : [];
  })(serviceCaller(withStyles(s)(BrandSetupWizard)))
);
